import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ArrowLeftIcon, ChevronDownIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { NotFound } from './NotFound';
import { posts } from '../data/posts';
import { PostBlock } from '../types/content';
export function BlogPost() {
  const {
    slug
  } = useParams();
  const post = posts.find(p => p.slug === slug);
  if (!post) return <NotFound data-id="element-270" data-mp="3fe37w0mkdokz" />;
  const recent = posts.filter(p => p.slug !== post.slug).slice(0, 4);
  return <>
      <section className="relative isolate overflow-hidden" data-id="element-271" data-mp="36frks8kwp8xa">
        <img src={post.image} alt="" className="absolute inset-0 -z-10 h-full w-full object-cover" data-id="element-272" data-mp="36gb15kuwrp97" />
        <div className="absolute inset-0 -z-10 bg-ink/80" data-id="element-273" data-mp="31i9bui6w2tpa" />
        <div className="mx-auto max-w-4xl px-6 pb-16 pt-36 xl:pt-48" data-id="element-274" data-mp="14oxwmwwurjdo">
          <Link to="/blog" className="inline-flex items-center gap-2 font-slab text-xs font-bold uppercase tracking-[0.2em] text-ink-muted hover:text-white" data-id="element-275" data-mp="zpbnayk5ana5">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-276" data-mp="113ip669asypv" />
            Blog
          </Link>
          <h1 className="mt-6 font-slab text-3xl font-bold uppercase leading-tight tracking-[0.08em] md:text-5xl" data-id="element-277" data-mp="165q7reomvd16">{post.title}</h1>
          <p className="mt-5 font-script text-lg italic text-ink-muted" data-id="element-278" data-mp="s6qjw98y3262">
            <time dateTime={post.date} data-id="element-279" data-mp="x3qp6hxz00w4">{format(parseISO(post.date), 'MMMM d, yyyy')}</time> · {post.category}
          </p>
        </div>
      </section>

      <div className="mx-auto grid max-w-6xl gap-16 px-6 py-16 lg:grid-cols-[minmax(0,1fr)_280px]" data-id="element-280" data-mp="1ov69u6fyvdjp">
        <article className="max-w-[68ch]" data-id="element-281" data-mp="1b3rr80pz1ed6">
          {post.blocks.map((block, i) => <Block key={i} block={block} data-id="element-282" data-mp="1g2312bg9nove" data-mpi={i} />)}

          {post.faqs && post.faqs.length > 0 && <section className="mt-14 border-t border-ink-line pt-10" aria-labelledby="faq-heading" data-id="element-283" data-mp="23rdt94jjwaxx">
              <h2 id="faq-heading" className="font-slab text-2xl font-bold uppercase tracking-[0.1em]" data-id="element-284" data-mp="28j5nbp1x0yst">
                FAQs
              </h2>
              <div className="mt-6 divide-y divide-ink-line border-y border-ink-line" data-id="element-285" data-mp="2iduou3znqt59">
                {post.faqs.map(faq => <details key={faq.q} className="group py-5" data-id="element-286" data-mp="28gg6fyu6buab">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-slab font-bold tracking-wide" data-id="element-287" data-mp="28evfskazcvhq">
                      {faq.q}
                      <ChevronDownIcon className="h-4 w-4 shrink-0 transition-transform duration-200 ease-out group-open:rotate-180" aria-hidden="true" data-id="element-288" data-mp="1zgtgqaat4cx3" />
                    </summary>
                    <p className="mt-3 leading-relaxed text-ink-muted" data-id="element-289" data-mp="1ppfc5o400mx3">{faq.a}</p>
                  </details>)}
              </div>
            </section>}

          <div className="mt-14 border border-ink-line p-8" data-id="element-290" data-mp="1frrdin39uv9n">
            <p className="font-slab text-lg font-bold uppercase tracking-[0.12em]" data-id="element-291" data-mp="1fr974xmwb0o0">Ready for your next tattoo?</p>
            <p className="mt-2 leading-relaxed text-ink-muted" data-id="element-292" data-mp="1bsa7nsjxnw3y">Walk-ins are welcome most every day, or request an appointment with your favorite artist.</p>
            <Button to="/book-now" className="mt-6" data-id="element-293" data-mp="3re31cq9ynwc8">
              Book now
            </Button>
          </div>
        </article>

        <aside className="lg:sticky lg:top-8 lg:h-fit" data-id="element-294" data-mp="9mgjv5a1zin">
          <h2 className="font-slab text-sm font-bold uppercase tracking-[0.2em]" data-id="element-295" data-mp="5864u27cfne6">Recent posts</h2>
          <ul className="mt-5 divide-y divide-ink-line border-y border-ink-line" data-id="element-296" data-mp="33q1vxq82ww11">
            {recent.map(p => <li key={p.slug} className="py-4" data-id="element-297" data-mp="38po8ryjzwa9b">
                <Link to={`/blog/${p.slug}`} className="group block" data-id="element-298" data-mp="38mabv5u3ot99">
                  <span className="block font-slab text-sm font-bold leading-snug group-hover:underline" data-id="element-299" data-mp="3dmeqeqbg7dwu">{p.title}</span>
                  <time dateTime={p.date} className="mt-1 block font-script text-sm italic text-ink-muted" data-id="element-300" data-mp="3ig98jpbenktt">
                    {format(parseISO(p.date), 'MMMM d, yyyy')}
                  </time>
                </Link>
              </li>)}
          </ul>
        </aside>
      </div>
    </>;
}
function Block({
  block
}: {
  block: PostBlock;
}) {
  switch (block.type) {
    case 'h2':
      return <h2 className="mb-4 mt-12 font-slab text-2xl font-bold uppercase leading-snug tracking-[0.08em]" data-id="element-301" data-mp="reic43833wr3">{block.text}</h2>;
    case 'h3':
      return <h3 className="mb-3 mt-8 font-slab text-lg font-bold tracking-wide" data-id="element-302" data-mp="10fymwc2ta0vu">{block.text}</h3>;
    case 'ul':
      return <ul className="my-6 list-disc space-y-2 pl-6 text-lg leading-relaxed text-ink-muted marker:text-white" data-id="element-303" data-mp="1f4co6ikkxhaa">
          {block.items.map(item => <li key={item} data-id="element-304" data-mp="1o741yp0xgp53">{item}</li>)}
        </ul>;
    default:
      return <p className="mb-5 text-lg leading-relaxed text-ink-muted" data-id="element-305" data-mp="1j7r3t84lvvn7">{block.text}</p>;
  }
}